'use client';
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Star, Shield, Zap, Circle, Check, X, Crown, CreditCard, Calendar, AlertCircle } from 'lucide-react';


const currentTier = 'featured';

const tiers = [
  {
    id: 'sub-tier-free',
    value: 'free',
    name: 'Free',
    price: '£0',
    period: 'forever',
    icon: Circle,
    color: 'text-muted-foreground',
    borderColor: 'border-border',
    features: ['Name listing only', 'Category listing', 'Phone number'],
    missing: ['Individual page', 'Photos', 'Verification', 'Analytics'],
  },
  {
    id: 'sub-tier-standard',
    value: 'standard',
    name: 'Standard',
    price: '£79',
    period: '/year',
    icon: Zap,
    color: 'text-accent',
    borderColor: 'border-accent/30',
    features: ['Full business page', 'Photo gallery', 'Website & social links', 'Contact details'],
    missing: ['Council verification', 'Analytics', 'Priority placement'],
  },
  {
    id: 'sub-tier-enhanced',
    value: 'enhanced',
    name: 'Enhanced',
    price: '£149',
    period: '/year',
    icon: Shield,
    color: 'text-purple-600',
    borderColor: 'border-purple-300',
    features: ['Everything in Standard', 'Council verification badge', 'Enhanced search visibility'],
    missing: ['Analytics dashboard', 'Priority placement'],
  },
  {
    id: 'sub-tier-featured',
    value: 'featured',
    name: 'Featured',
    price: '£299',
    period: '/year',
    icon: Star,
    color: 'text-warning',
    borderColor: 'border-warning/50',
    features: ['Everything in Enhanced', 'Priority search placement', 'In-depth analytics', 'Competitor comparison'],
    missing: [],
  },
];

export default function SubscriptionTab() {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    // Backend integration point: POST /api/subscriptions/{id}/cancel
    await new Promise((r) => setTimeout(r, 1200));
    toast?.success('Subscription cancellation scheduled for end of billing period');
    setShowCancelModal(false);
    setCancelLoading(false);
  };

  return (
    <div>
      {/* Current plan card */}
      <div className="card-surface p-6 mb-5 border-2 border-warning/40">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
              <Crown size={22} className="text-warning" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <h3 className="font-bold text-lg text-foreground">Featured Plan</h3>
                <span className="text-xs font-bold bg-positive/10 text-positive px-2 py-0.5 rounded">Active</span>
              </div>
              <p className="text-sm text-muted-foreground">£299/year · Renews 14 September 2026</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-foreground tabular-nums">£299</div>
            <div className="text-xs text-muted-foreground">billed annually</div>
          </div>
        </div>

        <hr className="border-border my-5" />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
          <div className="flex items-center gap-3">
            <CreditCard size={16} className="text-muted-foreground shrink-0" />
            <div>
              <div className="text-xs text-muted-foreground">Payment method</div>
              <div className="text-sm font-semibold text-foreground">Visa •••• 4821</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Calendar size={16} className="text-muted-foreground shrink-0" />
            <div>
              <div className="text-xs text-muted-foreground">Next billing date</div>
              <div className="text-sm font-semibold text-foreground">14 September 2026</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Star size={16} className="text-warning shrink-0" />
            <div>
              <div className="text-xs text-muted-foreground">Member since</div>
              <div className="text-sm font-semibold text-foreground">March 2019</div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button className="btn-secondary text-sm">
            <CreditCard size={13} />
            Update Payment Method
          </button>
          <button className="btn-secondary text-sm">
            Download Invoices
          </button>
          <button
            onClick={() => setShowCancelModal(true)}
            className="text-sm font-semibold text-negative hover:underline px-3 py-2"
          >
            Cancel Subscription
          </button>
        </div>
      </div>
      {/* Plan comparison */}
      <div className="card-surface p-6 mb-6">
        <h3 className="font-bold text-base text-foreground mb-5 pb-3 border-b border-border">
          Compare Plans
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {tiers?.map((tier) => {
            const isCurrent = tier?.value === currentTier;
            return (
              <div
                key={tier?.id}
                className={`rounded border-2 p-4 flex flex-col ${
                  isCurrent ? `${tier?.borderColor} bg-warning/5` : 'border-border'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <tier.icon size={16} className={tier?.color} />
                  <span className="font-bold text-sm text-foreground">{tier?.name}</span>
                  {isCurrent && (
                    <span className="ml-auto text-xs bg-warning/10 text-warning font-bold px-1.5 py-0.5 rounded">
                      Current
                    </span>
                  )}
                </div>
                <div className="mb-3">
                  <span className="text-xl font-bold text-foreground tabular-nums">{tier?.price}</span>
                  <span className="text-xs text-muted-foreground">{tier?.period}</span>
                </div>
                <ul className="space-y-1.5 flex-1 mb-4">
                  {tier?.features?.map((feat) => (
                    <li key={`${tier?.id}-feat-${feat}`} className="flex items-start gap-1.5 text-xs">
                      <Check size={11} className="text-positive mt-0.5 shrink-0" />
                      <span className="text-foreground">{feat}</span>
                    </li>
                  ))}
                  {tier?.missing?.map((feat) => (
                    <li key={`${tier?.id}-miss-${feat}`} className="flex items-start gap-1.5 text-xs">
                      <X size={11} className="text-muted-foreground mt-0.5 shrink-0" />
                      <span className="text-muted-foreground">{feat}</span>
                    </li>
                  ))}
                </ul>
                {!isCurrent && (
                  <button
                    className={`text-xs font-semibold py-2 px-3 rounded border transition-colors ${
                      tier?.value === 'free' ?'border-negative/30 text-negative hover:bg-negative/10' :'border-primary/30 text-primary hover:bg-primary/10'
                    }`}
                  >
                    {tier?.value === 'free' ?'Downgrade to Free'
                      : tier?.value === 'standard'|| tier?.value === 'enhanced' ?'Downgrade' :'Upgrade'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {/* Cancel confirmation modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="card-surface max-w-md w-full p-6 shadow-xl">
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle size={22} className="text-negative shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-base text-foreground mb-1">
                  Cancel Featured subscription?
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Your subscription will remain active until <strong>14 September 2026</strong>.
                  After that date, your listing will revert to the Free tier and your analytics
                  data, council verification badge, and priority placement will be removed.
                </p>
              </div>
            </div>
            <div className="bg-warning/10 border border-warning/30 rounded p-3 mb-5">
              <p className="text-xs text-warning font-semibold">
                You will lose: priority placement, analytics dashboard, council verification badge,
                and enhanced search visibility.
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowCancelModal(false)}
                className="btn-secondary text-sm"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={cancelLoading}
                className="bg-negative text-white text-sm font-semibold px-4 py-2 rounded border-none cursor-pointer flex items-center gap-2 hover:bg-red-700 transition-colors disabled:opacity-60"
              >
                {cancelLoading ? (
                  <><Star size={14} className="animate-spin" />Cancelling...</>
                ) : (
                  'Confirm Cancellation'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}